import { Link } from 'react-router-dom';
import { Ruler, MessageCircle, ArrowRight, CircleDot } from 'lucide-react';

const SIZE_CHART = [
  { size: 'S',    eu: '36', bust: '82-86',   waist: '62-66', hips: '88-92' },
  { size: 'M',    eu: '38', bust: '86-90',   waist: '66-70', hips: '92-96' },
  { size: 'L',    eu: '40', bust: '90-94',   waist: '70-74', hips: '96-100' },
  { size: 'XL',   eu: '42', bust: '94-98',   waist: '74-78', hips: '100-104' },
  { size: 'XXL',  eu: '44', bust: '98-102',  waist: '78-82', hips: '104-108' },
  { size: 'XXXL', eu: '46', bust: '102-108', waist: '82-88', hips: '108-114' },
];

const MeasureIcon = ({ number, label, color }) => (
  <div className="flex flex-col items-center">
    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-5 ${color}`}>
      <span className="font-serif text-3xl text-white">{number}</span>
    </div>
    <h3 className="font-serif text-lg mb-1">{label}</h3>
  </div>
);

export default function SizeGuidePage() {
  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="size-guide-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Guide des Tailles</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="bg-[#1A1514] text-white py-16 mb-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Ruler size={40} className="mx-auto mb-6 text-[#D4AF37]" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4" data-testid="size-guide-title">Guide des Tailles</h1>
          <p className="text-gray-300 max-w-xl mx-auto text-lg">
            Trouve la taille parfaite pour toi. Nos tailles sont basées sur les standards européens.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* How to Measure — Illustrated */}
        <section className="mb-16" data-testid="how-to-measure">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Étape par étape</p>
            <h2 className="font-serif text-3xl">Comment prendre tes mesures</h2>
          </div>

          <div className="bg-white border border-[#E0DFDD] p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-start gap-6 mb-8 pb-8 border-b border-[#E0DFDD]">
              <p className="text-sm text-[#6B6A68] leading-relaxed">
                Utilise un <strong className="text-[#1A1514]">mètre ruban souple</strong>. Tiens-toi droite et détendue, en sous-vêtements fins. 
                Le mètre ruban doit être bien à plat contre la peau, ni trop serré ni trop lâche. 
                Mesure en <strong className="text-[#1A1514]">centimètres</strong>.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-10">
              {/* Bust */}
              <div className="text-center">
                <MeasureIcon number="1" label="Tour de poitrine" color="bg-[#D4AF37]" />
                <div className="mt-4 relative">
                  <div className="w-32 h-32 mx-auto border-2 border-dashed border-[#D4AF37] rounded-full flex items-center justify-center mb-4">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-[#D4AF37]">
                      <ellipse cx="24" cy="20" rx="14" ry="16" stroke="currentColor" strokeWidth="2"/>
                      <path d="M10 20 C10 20, 16 28, 24 28 C32 28, 38 20, 38 20" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3"/>
                      <line x1="8" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="text-sm text-[#6B6A68] leading-relaxed">
                    Passe le mètre ruban autour de la partie <strong className="text-[#1A1514]">la plus forte</strong> de ta poitrine, au niveau des mamelons. Garde le ruban bien horizontal dans le dos.
                  </p>
                </div>
              </div>

              {/* Waist */}
              <div className="text-center">
                <MeasureIcon number="2" label="Tour de taille" color="bg-[#C87979]" />
                <div className="mt-4 relative">
                  <div className="w-32 h-32 mx-auto border-2 border-dashed border-[#C87979] rounded-full flex items-center justify-center mb-4">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-[#C87979]">
                      <path d="M14 8 C14 8, 10 24, 14 40" stroke="currentColor" strokeWidth="2"/>
                      <path d="M34 8 C34 8, 38 24, 34 40" stroke="currentColor" strokeWidth="2"/>
                      <line x1="10" y1="24" x2="38" y2="24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      <path d="M14 24 C14 24, 20 22, 24 22 C28 22, 34 24, 34 24" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3"/>
                    </svg>
                  </div>
                  <p className="text-sm text-[#6B6A68] leading-relaxed">
                    Mesure à la partie <strong className="text-[#1A1514]">la plus fine</strong> de ton buste, environ 2-3 cm au-dessus du nombril. Ne rentre pas le ventre.
                  </p>
                </div>
              </div>

              {/* Hips */}
              <div className="text-center">
                <MeasureIcon number="3" label="Tour de hanches" color="bg-[#1A1514]" />
                <div className="mt-4 relative">
                  <div className="w-32 h-32 mx-auto border-2 border-dashed border-[#1A1514] rounded-full flex items-center justify-center mb-4">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-[#1A1514]">
                      <path d="M16 8 C16 8, 10 20, 10 30 C10 38, 14 42, 18 42" stroke="currentColor" strokeWidth="2"/>
                      <path d="M32 8 C32 8, 38 20, 38 30 C38 38, 34 42, 30 42" stroke="currentColor" strokeWidth="2"/>
                      <line x1="8" y1="30" x2="40" y2="30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      <path d="M10 30 C10 30, 18 28, 24 28 C30 28, 38 30, 38 30" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3"/>
                    </svg>
                  </div>
                  <p className="text-sm text-[#6B6A68] leading-relaxed">
                    Mesure à l'endroit <strong className="text-[#1A1514]">le plus large</strong> de tes hanches et fessiers. Pieds joints, le ruban bien horizontal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Visual Size Chart */}
        <section className="mb-16" data-testid="size-chart-section">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Standards européens</p>
            <h2 className="font-serif text-3xl">Tableau des Tailles</h2>
            <p className="text-sm text-[#6B6A68] mt-2">Toutes les mesures sont en centimètres (cm)</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white border border-[#E0DFDD]" data-testid="size-chart">
              <thead>
                <tr className="bg-[#1A1514] text-white">
                  <th className="py-5 px-4 text-left text-xs uppercase tracking-wider font-medium">Taille</th>
                  <th className="py-5 px-4 text-left text-xs uppercase tracking-wider font-medium">EU</th>
                  <th className="py-5 px-4 text-center text-xs uppercase tracking-wider font-medium">
                    <span className="flex items-center justify-center gap-2">
                      <CircleDot size={14} className="text-[#D4AF37]" />
                      Tour de poitrine
                    </span>
                  </th>
                  <th className="py-5 px-4 text-center text-xs uppercase tracking-wider font-medium">
                    <span className="flex items-center justify-center gap-2">
                      <CircleDot size={14} className="text-[#C87979]" />
                      Tour de taille
                    </span>
                  </th>
                  <th className="py-5 px-4 text-center text-xs uppercase tracking-wider font-medium">
                    <span className="flex items-center justify-center gap-2">
                      <CircleDot size={14} className="text-gray-400" />
                      Tour de hanches
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {SIZE_CHART.map((row, idx) => (
                  <tr 
                    key={row.size} 
                    className={`border-b border-[#E0DFDD] transition-colors hover:bg-[#F4E3E1]/30 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FCFBF9]'}`}
                    data-testid={`size-row-${row.size}`}
                  >
                    <td className="py-5 px-4">
                      <span className="inline-flex items-center justify-center w-14 h-10 bg-[#1A1514] text-white text-sm font-bold tracking-wider">
                        {row.size}
                      </span>
                    </td>
                    <td className="py-5 px-4 text-sm text-[#6B6A68] font-medium">{row.eu}</td>
                    <td className="py-5 px-4 text-center">
                      <span className="text-sm font-medium text-[#1A1514]">{row.bust}</span>
                      <span className="text-xs text-[#6B6A68] ml-1">cm</span>
                    </td>
                    <td className="py-5 px-4 text-center">
                      <span className="text-sm font-medium text-[#1A1514]">{row.waist}</span>
                      <span className="text-xs text-[#6B6A68] ml-1">cm</span>
                    </td>
                    <td className="py-5 px-4 text-center">
                      <span className="text-sm font-medium text-[#1A1514]">{row.hips}</span>
                      <span className="text-xs text-[#6B6A68] ml-1">cm</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Tips */}
        <section className="mb-16" data-testid="tips-section">
          <div className="bg-[#F4E3E1] p-8 md:p-10">
            <h2 className="font-serif text-2xl mb-6">Nos conseils</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  <strong className="text-[#1A1514]">Entre deux tailles ?</strong> Choisis la plus grande pour plus de confort au quotidien.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  <strong className="text-[#1A1514]">Articles en dentelle :</strong> considère un ajustement plus près du corps pour un tombé élégant.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  <strong className="text-[#1A1514]">Pyjamas & Kimonos :</strong> les tailles sont conçues pour être amples et confortables.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  <strong className="text-[#1A1514]">Échange sous 48h :</strong> si la taille ne convient pas, on s'en occupe.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WhatsApp Help CTA */}
        <section className="mb-16" data-testid="whatsapp-help-section">
          <div className="bg-white border-2 border-[#D4AF37] p-8 md:p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-[#25D366] rounded-full flex items-center justify-center">
              <MessageCircle size={28} className="text-white" fill="white" />
            </div>
            <h2 className="font-serif text-2xl md:text-3xl mb-3">Toujours pas sure de ta taille ?</h2>
            <p className="text-[#6B6A68] mb-8 max-w-lg mx-auto leading-relaxed">
              Contacte-nous sur WhatsApp, on t'aide à choisir ta taille <strong className="text-[#1A1514]">gratuitement</strong>. 
              Envoie-nous tes mensurations et on te conseille le meilleur choix.
            </p>
            <a 
              href="https://wa.me/213782367184?text=Bonjour, j'ai besoin d'aide pour choisir ma taille"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 text-sm font-medium uppercase tracking-wider hover:bg-[#1da851] transition-colors"
              data-testid="whatsapp-help-btn"
            >
              <MessageCircle size={18} fill="white" />
              Contacte-nous sur WhatsApp
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
